import { Link } from "react-router";
import { Button, Card, Cluster, Stack } from "@salesapp/ui";
import type { Contact } from "../../lib/crmApi";
import { FIELD_HINTS } from "../../help/fieldHints";
import { useCrmHref } from "../../lib/customerUrl";
import { AskLoxleyButton } from "../../loxley/AskLoxleyButton";
import { DetailSection, FieldItem } from "./RecordWorkspace";
import { RecordAuditInfo } from "./RecordAuditInfo";

export function contactName(person: Pick<Contact, "first_name" | "last_name">): string {
  return `${person.first_name} ${person.last_name ?? ""}`.trim();
}

/**
 * A contact in view mode: every field read-only, with Edit as a deliberate second step so a
 * click to look someone up can never change them by accident.
 */
export function ContactViewCard({
  person,
  showCompany = false,
  onEdit,
  onClose,
}: {
  person: Contact;
  /** Link to the company; off on the company's own page. */
  showCompany?: boolean;
  onEdit: () => void;
  onClose: () => void;
}) {
  const crmHref = useCrmHref();
  const name = contactName(person);
  const roles = [person.is_primary ? "Primary" : null, person.is_billing_contact ? "Billing" : null].filter(Boolean);
  return (
    <Card className="crm-record-view">
      <Stack gap={3}>
        <Cluster gap={2} justify="space-between" align="center">
          <h2 className="crm-section-title">{name}</h2>
          <Cluster gap={2}>
            <Button size="sm" onClick={onEdit}>Edit</Button>
            <AskLoxleyButton record={{ kind: "contact", id: person.id, label: name }} />
            <Button size="sm" variant="ghost" onClick={onClose}>Close</Button>
          </Cluster>
        </Cluster>
        <DetailSection title="Contact details">
          {showCompany ? (
            <FieldItem
              label="Company"
              value={<Link to={crmHref(`/companies/${person.company_id}`)}>{person.company_name ?? "Open company"}</Link>}
            />
          ) : null}
          <FieldItem label="First name" hint={FIELD_HINTS.contact_first_name} value={person.first_name} />
          <FieldItem label="Last name" hint={FIELD_HINTS.contact_last_name} value={person.last_name || null} />
          <FieldItem label="Job title" value={person.job_title || null} />
          <FieldItem label="Department" value={person.department || null} />
          <FieldItem label="Work email" hint={FIELD_HINTS.contact_work_email} value={person.work_email || person.email || null} />
          <FieldItem label="Personal email" hint={FIELD_HINTS.contact_personal_email} value={person.personal_email || null} />
          <FieldItem label="Mobile" value={person.mobile || null} />
          <FieldItem label="Phone" value={person.phone || null} />
          <FieldItem label="Marketing" value={person.marketing_enabled ? "Yes" : "No"} />
          <FieldItem label="Status" value={person.is_active === false ? "Inactive" : "Active"} />
          {roles.length ? <FieldItem label="Role" value={roles.join(", ")} /> : null}
          {person.notes ? <FieldItem label="Notes" value={person.notes} wide /> : null}
        </DetailSection>
        <RecordAuditInfo record={person} />
      </Stack>
    </Card>
  );
}
